import { FORBIDDEN, UNAUTHORIZED, TEMPORARY_REDIRECT, SERVICE_UNAVAILABLE } from 'http-status-codes';
import sessionStorageService, { storageKeys } from './sessionStorageService';
import { parseAsJson } from './requestHelpers';
import getCookie from './getCookie';

import config from '../config';
import routes from '../routes';
import { clientDisabledErrorMessage, maintenanceErrorMessage } from '../shared/constants';
import { savePathToRedirectAfterLoginIfValid } from '../shared/redirectAfterLogin';

const {
  BASE_PATH,
  CSRF_TOKEN_COOKIE_NAME,
  CSRF_TOKEN_HEADER_NAME,
} = config;
const methodsWithHeaders = ['POST', 'PUT', 'PATCH', 'DELETE'];

export const redirectToAuthorize = response => {
  savePathToRedirectAfterLoginIfValid();
  const apiURL = `${BASE_PATH}/api/authenticate`;

  return window.location.replace(apiURL) || response;
};

const handleTemporaryRedirect = response =>
  parseAsJson(response)
    .then(({ redirectUrl }) => window.location.replace(`${BASE_PATH}${redirectUrl}`));

const redirectToAccessDenied = response => {
  // Save current path before redirecting (same as redirectToAuthorize for consistency)
  savePathToRedirectAfterLoginIfValid();

  const currentURL = window.location.href;
  const accessDeniedPageUrl = `${window.location.origin}${BASE_PATH}${routes.ACCESS_DENIED}`;

  if (currentURL !== accessDeniedPageUrl) {
    return window.location.replace(accessDeniedPageUrl);
  }

  return response;
};

const redirectToMaintenancePage = response => {
  const currentURL = window.location.href;
  const maintenancePageUrl = `${window.location.origin}${BASE_PATH}${routes.MAINTENANCE}`;

  if (currentURL !== maintenancePageUrl) {
    return window.location.replace(maintenancePageUrl);
  }

  return response;
};

const redirectToChangeClientPage = (response, nextClient) => {
  const currentURL = window.location.href;
  const changeClientPageUrl = `${window.location.origin}${BASE_PATH}${routes.CHANGE_CLIENT}`.replace(/:clientId/, nextClient);

  if (currentURL !== changeClientPageUrl) {
    return window.location.replace(changeClientPageUrl);
  }

  return response;
};

async function fetchService(url, config = {}, forceCookie= false) {
  const currentClientId = await sessionStorageService.getItem(storageKeys.CLIENT_KEY);
  const sessionToken = window.sessionStorage.getItem('sessionToken');

  const validSessionToken = getCookie('validSessionToken');
  if (!sessionToken && forceCookie && !validSessionToken) {
    return redirectToAuthorize();
  }

  const contentType = config.body && config.body instanceof FormData ? {} : { 'Content-Type': 'application/json' };
  const csrfHeader = { [CSRF_TOKEN_HEADER_NAME]: getCookie(CSRF_TOKEN_COOKIE_NAME) };
  const headers = methodsWithHeaders.includes(config.method)
    ? {
      Accept: 'application/json',
      Client: currentClientId,
      sessionToken,
      ...contentType,
      ...config.headers,
      ...csrfHeader,
    }
    : {
      Client: currentClientId,
      sessionToken,
      ...config.headers,
    };

  const params = {
    ...config,
    credentials: 'same-origin',
    headers,
  };

  try {
    const response = await fetch(`${BASE_PATH}${url}`, params);
    switch (response.status) {
      case UNAUTHORIZED:
        return redirectToAuthorize(response);
      case FORBIDDEN:
        return redirectToAccessDenied(response);
      case SERVICE_UNAVAILABLE: {
        const responseBody = await response.json();

        if (responseBody?.message === maintenanceErrorMessage) {
          return redirectToMaintenancePage(response);
        }
        if (responseBody?.message === clientDisabledErrorMessage) {
          return redirectToChangeClientPage(response, responseBody.details?.nextClient);
        }
        return response;
      }
      case TEMPORARY_REDIRECT:
        return handleTemporaryRedirect(response);
      default:
        return response;
    }
  } catch (error) {
    if (error instanceof DOMException) {
      // Fallback case for Unauthorized responses using IE.
      // https://github.com/github/fetch/issues/409
      return redirectToAuthorize();
    }

    throw error;
  }
}

export default fetchService;
